// @ts-check

import { markProjectModelChanged } from '../project/project_revision_service.js';
import { isPreparingProjectMutation } from '../project/project_mutation_scope.mjs';

const DEFAULT_CAPABILITIES = Object.freeze({
  selectable: true,
  movable: true,
  rotatable: true,
  scalable: false,
  copyable: true,
  deletable: true
});

const objectsByOwner = new Map();
const objectsByLocalId = new Map();
const objectsByIdentity = new Map();
let drawingObjectRegistryRevision = 0;

function nextRevision(current) {
  return current >= Number.MAX_SAFE_INTEGER ? 1 : current + 1;
}

function markDrawingObjectRegistryChanged() {
  drawingObjectRegistryRevision = nextRevision(drawingObjectRegistryRevision);
  return drawingObjectRegistryRevision;
}

function drawingObjectHookLabel(object = {}) {
  try {
    return `${String(object?.systemType || '(ohne Owner)')}:${String(object?.id || '(ohne ID)')}`;
  } catch {
    return '(unlesbares DrawingObject)';
  }
}

function warnDrawingObjectHook(object, hookName, error) {
  console.warn(`DrawingObject ${drawingObjectHookLabel(object)}: ${hookName} fehlgeschlagen.`, error);
}

function isolateUnexpectedThenable(object, hookName, value) {
  if (!value || (typeof value !== 'object' && typeof value !== 'function')) return false;
  let then;
  try {
    then = value.then;
  } catch (error) {
    warnDrawingObjectHook(object, `${hookName}:thenable`, error);
    return true;
  }
  if (typeof then !== 'function') return false;
  try {
    Promise.resolve(value).then(
      () => undefined,
      error => warnDrawingObjectHook(object, `${hookName}:thenable`, error)
    );
  } catch (error) {
    warnDrawingObjectHook(object, `${hookName}:thenable`, error);
  }
  return true;
}

function normalizeIdentityPart(value) {
  return String(value || '').trim();
}

function ownerIdentity(value = {}) {
  if (typeof value === 'string') {
    return { owner: normalizeIdentityPart(value), valid: true };
  }
  const owners = [...new Set([
    value?.systemType,
    value?.systemId,
    value?.systemKey,
    value?.system,
    value?.owner,
    value?.ownerId
  ].map(normalizeIdentityPart).filter(Boolean))];
  return {
    owner: owners.length === 1 ? owners[0] : '',
    valid: owners.length <= 1
  };
}

function lookupIdentity(idOrDescriptor, options = {}) {
  const descriptor = idOrDescriptor && typeof idOrDescriptor === 'object'
    ? idOrDescriptor
    : null;
  const descriptorIdentity = ownerIdentity(descriptor || {});
  const optionIdentity = ownerIdentity(options);
  return {
    id: normalizeIdentityPart(descriptor?.id ?? idOrDescriptor),
    systemType: descriptorIdentity.owner || optionIdentity.owner,
    valid: descriptorIdentity.valid
      && optionIdentity.valid
      && !(descriptorIdentity.owner && optionIdentity.owner && descriptorIdentity.owner !== optionIdentity.owner)
  };
}

export function canonicalDrawingObjectKey(systemType, id) {
  const owner = normalizeIdentityPart(systemType);
  const localId = normalizeIdentityPart(id);
  return owner && localId ? `${owner}:${localId}` : '';
}

function internalDrawingObjectKey(systemType, id) {
  return JSON.stringify([normalizeIdentityPart(systemType), normalizeIdentityPart(id)]);
}

function ownerObjects(systemType, create = false) {
  const owner = normalizeIdentityPart(systemType);
  if (!owner) return null;
  let objects = objectsByOwner.get(owner) || null;
  if (!objects && create) {
    objects = new Map();
    objectsByOwner.set(owner, objects);
  }
  return objects;
}

function localIdObjects(id, create = false) {
  const localId = normalizeIdentityPart(id);
  if (!localId) return null;
  let objects = objectsByLocalId.get(localId) || null;
  if (!objects && create) {
    objects = new Map();
    objectsByLocalId.set(localId, objects);
  }
  return objects;
}

function setDrawingObject(object) {
  objectsByIdentity.set(internalDrawingObjectKey(object.systemType, object.id), object);
  ownerObjects(object.systemType, true).set(object.id, object);
  localIdObjects(object.id, true).set(object.systemType, object);
}

function deleteDrawingObject(systemType, id) {
  const owner = normalizeIdentityPart(systemType);
  const localId = normalizeIdentityPart(id);
  const byOwner = ownerObjects(owner);
  const object = byOwner?.get(localId) || null;
  if (!object) return false;
  objectsByIdentity.delete(internalDrawingObjectKey(owner, localId));
  byOwner.delete(localId);
  if (!byOwner.size) objectsByOwner.delete(owner);
  const byLocalId = localIdObjects(localId);
  byLocalId?.delete(owner);
  if (byLocalId && !byLocalId.size) objectsByLocalId.delete(localId);
  return true;
}

function normalizeVector2(value = {}, fallback = {}) {
  return {
    x: Number.isFinite(Number(value.x)) ? Number(value.x) : Number(fallback.x) || 0,
    z: Number.isFinite(Number(value.z)) ? Number(value.z) : Number(fallback.z) || 0
  };
}

function optionalVector2(value = null) {
  if (!value || typeof value !== 'object') return null;
  const x = Number(value.x);
  const z = Number(value.z ?? value.y);
  return Number.isFinite(x) && Number.isFinite(z) ? { x, z } : null;
}

function normalizeBounds(bounds = null) {
  if (!bounds || typeof bounds !== 'object') return null;
  const minX = Number(bounds.minX ?? bounds.x);
  const minZ = Number(bounds.minZ ?? bounds.z ?? bounds.y);
  const maxX = Number.isFinite(Number(bounds.maxX))
    ? Number(bounds.maxX)
    : minX + Number(bounds.width);
  const maxZ = Number.isFinite(Number(bounds.maxZ))
    ? Number(bounds.maxZ)
    : minZ + Number(bounds.depth ?? bounds.height);
  if (![minX, minZ, maxX, maxZ].every(Number.isFinite) || maxX < minX || maxZ < minZ) return null;
  return { minX, minZ, maxX, maxZ };
}

function boundsCenter(bounds = null) {
  const normalized = normalizeBounds(bounds);
  return normalized
    ? { x: (normalized.minX + normalized.maxX) / 2, z: (normalized.minZ + normalized.maxZ) / 2 }
    : null;
}

function boundsCorners(bounds = null) {
  const normalized = normalizeBounds(bounds);
  return normalized
    ? [
        { x: normalized.minX, z: normalized.minZ },
        { x: normalized.maxX, z: normalized.minZ },
        { x: normalized.maxX, z: normalized.maxZ },
        { x: normalized.minX, z: normalized.maxZ }
      ]
    : [];
}

function rotatePoint(point, origin, angleDeg = 0) {
  const radians = (Number(angleDeg) || 0) * Math.PI / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = (Number(point?.x) || 0) - (Number(origin?.x) || 0);
  const dz = (Number(point?.z ?? point?.y) || 0) - (Number(origin?.z ?? origin?.y) || 0);
  return {
    x: (Number(origin?.x) || 0) + dx * cos - dz * sin,
    z: (Number(origin?.z ?? origin?.y) || 0) + dx * sin + dz * cos
  };
}

function normalizeScale(value = 1) {
  if (value && typeof value === 'object') {
    return {
      x: Number.isFinite(Number(value.x)) ? Number(value.x) : 1,
      y: Number.isFinite(Number(value.y)) ? Number(value.y) : 1,
      z: Number.isFinite(Number(value.z)) ? Number(value.z) : 1
    };
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 1;
}

function normalizeDrawingObject(raw = {}) {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.id || '').trim();
  const rawOwnerIdentity = ownerIdentity(raw);
  const systemType = rawOwnerIdentity.owner;
  if (!id || !systemType || !rawOwnerIdentity.valid) return null;
  return {
    id,
    type: String(raw.type || 'drawing-object'),
    systemType,
    object3D: raw.object3D || raw.group || null,
    group: raw.group || raw.object3D || null,
    position: normalizeVector2(raw.position || raw),
    rotation: Number.isFinite(Number(raw.rotation)) ? Number(raw.rotation) : 0,
    groupRotationPivot: optionalVector2(raw.groupRotationPivot || raw.metadata?.groupRotationPivot),
    rotationPivot: optionalVector2(raw.rotationPivot || raw.metadata?.rotationPivot),
    scale: normalizeScale(raw.scale),
    metadata: raw.metadata && typeof raw.metadata === 'object' ? { ...raw.metadata } : {},
    selectable: raw.selectable ?? DEFAULT_CAPABILITIES.selectable,
    movable: raw.movable ?? DEFAULT_CAPABILITIES.movable,
    rotatable: raw.rotatable ?? DEFAULT_CAPABILITIES.rotatable,
    scalable: raw.scalable ?? DEFAULT_CAPABILITIES.scalable,
    copyable: raw.copyable ?? DEFAULT_CAPABILITIES.copyable,
    deletable: raw.deletable ?? DEFAULT_CAPABILITIES.deletable,
    getState: typeof raw.getState === 'function' ? raw.getState : null,
    setState: typeof raw.setState === 'function' ? raw.setState : null,
    clone: typeof raw.clone === 'function' ? raw.clone : null,
    remove: typeof raw.remove === 'function' ? raw.remove : null,
    highlight: typeof raw.highlight === 'function' ? raw.highlight : null
  };
}

export function registerDrawingObject(raw) {
  const object = normalizeDrawingObject(raw);
  if (!object) return null;
  // Owner hooks may rebuild derived handles while preparing a deletion. Keep
  // these handles local until the host commits and refreshes the real drawing.
  if (isPreparingProjectMutation()) return object;
  setDrawingObject(object);
  markDrawingObjectRegistryChanged();
  return object;
}

export function unregisterDrawingObject(id, options = {}) {
  const identity = lookupIdentity(id, options);
  if (!identity.valid) return false;
  const object = identity.systemType
    ? getDrawingObjectForOwner(identity.systemType, identity.id)
    : getDrawingObject(identity.id);
  if (isPreparingProjectMutation()) return Boolean(object);
  const removed = object
    ? deleteDrawingObject(object.systemType, object.id)
    : false;
  if (removed) markDrawingObjectRegistryChanged();
  return removed;
}

export function unregisterDrawingObjectForOwner(systemType, id) {
  return unregisterDrawingObject(id, { systemType });
}

export function clearDrawingObjects(options = {}) {
  if (isPreparingProjectMutation()) return;
  const identity = ownerIdentity(options);
  if (!identity.valid) return;
  const systemType = identity.owner;
  if (!systemType) {
    if (!objectsByIdentity.size) return;
    objectsByIdentity.clear();
    objectsByOwner.clear();
    objectsByLocalId.clear();
    markDrawingObjectRegistryChanged();
    return;
  }
  const ids = [...(ownerObjects(systemType)?.keys() || [])];
  const changed = ids.reduce((removed, id) => deleteDrawingObject(systemType, id) || removed, false);
  if (changed) markDrawingObjectRegistryChanged();
}

export function getDrawingObjectForOwner(systemType, id) {
  const owner = normalizeIdentityPart(systemType);
  const localId = normalizeIdentityPart(id);
  return ownerObjects(owner)?.get(localId) || null;
}

export function getDrawingObject(id, options = {}) {
  const identity = lookupIdentity(id, options);
  if (!identity.valid || !identity.id) return null;
  if (identity.systemType) return getDrawingObjectForOwner(identity.systemType, identity.id);
  const candidates = localIdObjects(identity.id);
  return candidates?.size === 1 ? candidates.values().next().value || null : null;
}

export function allDrawingObjects(options = {}) {
  const identity = ownerIdentity(options);
  if (!identity.valid) return [];
  const systemType = identity.owner;
  if (systemType) return [...(ownerObjects(systemType)?.values() || [])];
  return [...objectsByIdentity.values()];
}

export function getDrawingObjectRegistryRevision() {
  return drawingObjectRegistryRevision;
}

export function updateDrawingObject(id, patch = {}, options = {}) {
  const identity = lookupIdentity(id, options);
  if (!identity.valid) return null;
  const current = identity.systemType
    ? getDrawingObjectForOwner(identity.systemType, identity.id)
    : getDrawingObject(identity.id);
  if (!current) return null;
  const next = normalizeDrawingObject({ ...current, ...patch, id: current.id, systemType: current.systemType });
  if (!next) return current;
  if (isPreparingProjectMutation()) return next;
  setDrawingObject(next);
  markDrawingObjectRegistryChanged();
  return next;
}

export function drawingObjectState(object) {
  if (!object) return null;
  try {
    const state = typeof object.getState === 'function' ? object.getState(object) : object;
    if (isolateUnexpectedThenable(object, 'getState', state) || !state) return null;
    const metadata = state.metadata && typeof state.metadata === 'object'
      ? { ...state.metadata }
      : { ...(object.metadata || {}) };
    const groupRotationPivot = optionalVector2(
      state.groupRotationPivot
        || object.groupRotationPivot
        || metadata.groupRotationPivot
        || metadata.rotationPivot
    );
    const rotationPivot = optionalVector2(
      state.rotationPivot
        || object.rotationPivot
        || metadata.rotationPivot
        || groupRotationPivot
    );
    return {
      id: String(object.id),
      type: String(state.type || object.type || 'drawing-object'),
      systemType: String(object.systemType),
      position: normalizeVector2(state.position || state, object.position),
      rotation: Number.isFinite(Number(state.rotation)) ? Number(state.rotation) : Number(object.rotation) || 0,
      groupRotationPivot,
      rotationPivot,
      scale: normalizeScale(state.scale ?? object.scale),
      metadata
    };
  } catch (error) {
    warnDrawingObjectHook(object, 'getState', error);
    return null;
  }
}

export function drawingObjectLocalBounds(objectOrState = {}) {
  const state = objectOrState?.getState ? drawingObjectState(objectOrState) : objectOrState;
  return normalizeBounds(state?.metadata?.localBounds)
    || null;
}

function drawingObjectStoredWorldBounds(objectOrState = {}) {
  const state = objectOrState?.getState ? drawingObjectState(objectOrState) : objectOrState;
  return normalizeBounds(state?.metadata?.bounds)
    || normalizeBounds(state?.bounds)
    || null;
}

export function drawingObjectCenter(objectOrState = {}) {
  const state = objectOrState?.getState ? drawingObjectState(objectOrState) : objectOrState;
  return boundsCenter(drawingObjectLocalBounds(state))
    || boundsCenter(drawingObjectStoredWorldBounds(state))
    || optionalVector2(state?.position || state);
}

export function drawingObjectRotationPivot(objectOrState = {}) {
  const state = objectOrState?.getState ? drawingObjectState(objectOrState) : objectOrState;
  return optionalVector2(state?.groupRotationPivot)
    || optionalVector2(state?.rotationPivot)
    || optionalVector2(state?.metadata?.groupRotationPivot)
    || optionalVector2(state?.metadata?.rotationPivot)
    || drawingObjectCenter(state);
}

export function drawingObjectWorldCorners(objectOrState = {}) {
  const state = objectOrState?.getState ? drawingObjectState(objectOrState) : objectOrState;
  const bounds = drawingObjectLocalBounds(state);
  if (bounds) {
    const center = boundsCenter(bounds);
    const rotation = Number(state?.rotation) || 0;
    return boundsCorners(bounds).map(point => rotatePoint(point, center, rotation));
  }
  const storedWorldBounds = drawingObjectStoredWorldBounds(state);
  if (storedWorldBounds) return boundsCorners(storedWorldBounds);
  const point = optionalVector2(state?.position || state);
  return point ? [point] : [];
}

export function drawingObjectWorldBounds(objectOrState = {}) {
  const points = drawingObjectWorldCorners(objectOrState);
  if (!points.length) return null;
  return {
    minX: Math.min(...points.map(point => point.x)),
    maxX: Math.max(...points.map(point => point.x)),
    minZ: Math.min(...points.map(point => point.z)),
    maxZ: Math.max(...points.map(point => point.z))
  };
}

export function applyDrawingObjectState(object, state = {}) {
  if (!object || !state) return false;
  if (isolateUnexpectedThenable(object, 'setState:input', state)) return false;
  try {
    const objectId = normalizeIdentityPart(object.id);
    const objectOwnerIdentity = ownerIdentity(object);
    const objectOwner = objectOwnerIdentity.owner;
    const stateId = normalizeIdentityPart(state.id);
    const stateOwnerIdentity = ownerIdentity(state);
    const stateOwner = stateOwnerIdentity.owner;
    if (!objectId || !objectOwner || !objectOwnerIdentity.valid || !stateOwnerIdentity.valid
      || (stateId && stateId !== objectId)
      || (stateOwner && stateOwner !== objectOwner)) return false;
    const ownerBoundState = { ...state, id: objectId, systemType: objectOwner };
    if (typeof object.setState === 'function') {
      const result = object.setState(ownerBoundState, object);
      if (isolateUnexpectedThenable(object, 'setState', result)) return false;
      const applied = result !== false;
      if (applied) markProjectModelChanged('drawing-object-state');
      return applied;
    }
    // A handle without a domain setter cannot contribute a staged document edit.
    if (isPreparingProjectMutation()) return false;
    Object.assign(object, {
      position: normalizeVector2(ownerBoundState.position || ownerBoundState, object.position),
      rotation: Number.isFinite(Number(ownerBoundState.rotation)) ? Number(ownerBoundState.rotation) : Number(object.rotation) || 0,
      groupRotationPivot: optionalVector2(ownerBoundState.groupRotationPivot || object.groupRotationPivot),
      rotationPivot: optionalVector2(ownerBoundState.rotationPivot || object.rotationPivot),
      scale: normalizeScale(ownerBoundState.scale ?? object.scale),
      metadata: ownerBoundState.metadata && typeof ownerBoundState.metadata === 'object'
        ? { ...ownerBoundState.metadata }
        : { ...(object.metadata || {}) }
    });
    markProjectModelChanged('drawing-object-state');
    return true;
  } catch (error) {
    warnDrawingObjectHook(object, 'setState', error);
    return false;
  }
}

export { DEFAULT_CAPABILITIES };
